import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@material-ui/icons";
import UserEditModal from "./UserEditModal";
import UserCreateModal from "./UserCreateModal";
import UserEditPasswordModal from "./UserEditPasswordModal";
import PrivilegesModal from "./PrivilegesModal";
import {
  fetchUsers,
  deleteUser,
  saveUser,
  updateUserPassword,
} from "./UserService";
import { toast } from "react-toastify";
import PasswordIcon from "@mui/icons-material/Password";
import { usePageStyles } from "../common/pageStyles";
import { COLORS } from "../UserDashboard/dashboardConfig";

const useStyles = makeStyles((theme) => ({
  tableWrap: { overflow: "auto", maxHeight: "calc(100vh - 220px)" },
  headCell: {
    background: "#f5f7fa",
    fontWeight: 700,
    color: COLORS.primaryDark,
    whiteSpace: "nowrap",
  },
  actionCell: { whiteSpace: "nowrap" },
  empty: { padding: theme.spacing(4), textAlign: "center", color: COLORS.textMuted },
}));

const AdminPanel = () => {
  const classes = useStyles();
  const page = usePageStyles();

  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // null for new user, object for edit
  const [privilegesModalOpen, setPrivilegesModalOpen] = useState(false); // Nowy stan dla modala przywilejów

  const loadUsers = async () => {
    try {
      const usersData = await fetchUsers();
      setUsers(usersData);
    } catch (error) {
      toast.error("Problem z pobraniem użytkowników.");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers();
    }, 500); // Opóźnienie 1 sekundy

    return () => clearTimeout(timer); // Oczyszczenie timera przy odmontowywaniu komponentu
  }, []);

  const handleUserSubmit = async (user, isEditing) => {
    try {
      await saveUser(
        user,
        isEditing ? "PUT" : "POST",
        isEditing ? user.id : undefined
      );
      toast.success(
        `Użytkownik został ${isEditing ? "zaktualizowany" : "utworzony"}!`
      );
      loadUsers();
    } catch (error) {
      toast.error(
        `Problem z ${isEditing ? "aktualizacją" : "dodaniem"} użytkownika.`
      );
    } finally {
      setModalOpen(false);
      setCurrentUser(null);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await deleteUser(userId);
      toast.success("Użytkownik usunięty.");
      loadUsers();
    } catch (error) {
      toast.error("Problem z usunięciem użytkownika.");
    }
  };

  const openModalForEdit = (user) => {
    setCurrentUser(user);
    setModalOpen(true);
  };

  const openModalForNew = () => {
    setCurrentUser(null); // Ensure modal opens for new user creation
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentUser(null);
  };

  const openPasswordModal = (user) => {
    setCurrentUser(user);
    setPasswordModalOpen(true); // Otwórz modal zmiany hasła
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setCurrentUser(null);
  };

  const openPrivilegesModal = () => {
    setPrivilegesModalOpen(true);
  };

  const closePrivilegesModal = () => {
    setPrivilegesModalOpen(false);
  };

  const handlePasswordSubmit = async (editedUser) => {
    try {
      await updateUserPassword(editedUser.id, editedUser.password);
      toast.success("Hasło zostało pomyślnie zaktualizowane."); // Informuj użytkownika o sukcesie
      loadUsers(); // Opcjonalnie, odśwież listę użytkowników jeśli jest taka potrzeba
    } catch (error) {
      console.error("Błąd aktualizacji hasła:", error);
      toast.error("Nie udało się zaktualizować hasła."); // Informuj użytkownika o błędzie
    } finally {
      closePasswordModal(); // Zamknij modal niezależnie od wyniku operacji
    }
  };

  return (
    <div>
      <div className={page.pageHeader}>
        <div>
          <h1 className={page.title}>Admin panel</h1>
          <div className={page.subtitle}>
            Zarządzanie użytkownikami i przywilejami
          </div>
        </div>
        <div className={page.headerActions}>
          <Button variant="outlined" color="primary" onClick={openPrivilegesModal}>
            Zarządzaj przywilejami
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openModalForNew}
          >
            Dodaj użytkownika
          </Button>
        </div>
      </div>

      <div className={page.card}>
        <div className={classes.tableWrap}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell}>Username</TableCell>
                <TableCell className={classes.headCell}>Email</TableCell>
                <TableCell className={classes.headCell}>Rola</TableCell>
                <TableCell className={classes.headCell}>Data dodania</TableCell>
                <TableCell className={classes.headCell}>Dostęp</TableCell>
                <TableCell className={classes.headCell}>Data odebrania dostępu</TableCell>
                <TableCell className={classes.headCell}>Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const formattedDateAdded = user.date_added
                  ? new Date(user.date_added).toLocaleDateString("pl-PL", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : "";
                const formattedDateDenied = user.access_denied
                  ? new Date(user.access_denied).toLocaleDateString("pl-PL", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : "";

                return (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{formattedDateAdded}</TableCell>
                    <TableCell>{user.access}</TableCell>
                    <TableCell>{formattedDateDenied}</TableCell>
                    <TableCell className={classes.actionCell}>
                      <Tooltip title="Edytuj">
                        <IconButton size="small" onClick={() => openModalForEdit(user)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zmień hasło">
                        <IconButton size="small" onClick={() => openPasswordModal(user)}>
                          <PasswordIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Usuń">
                        <IconButton size="small" onClick={() => handleDelete(user.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
        {modalOpen &&
          (currentUser ? (
            <UserEditModal
              user={currentUser}
              open={modalOpen}
              onClose={closeModal}
              onSave={(user) => handleUserSubmit(user, true)}
            />
          ) : (
            <UserCreateModal
              open={modalOpen}
              onClose={closeModal}
              onSave={(user) => handleUserSubmit(user, false)}
            />
          ))}
        {passwordModalOpen && (
          <UserEditPasswordModal
            user={currentUser}
            open={passwordModalOpen}
            onClose={closePasswordModal}
            onSave={handlePasswordSubmit}
          />
        )}
        {privilegesModalOpen && (
          <PrivilegesModal
            open={privilegesModalOpen}
            onClose={closePrivilegesModal}
            users={users} // Przekaż dane użytkowników do modala
          />
        )}
    </div>
  );
};

export default AdminPanel;
